// The monorail, laid out from the curve Sam draws in Kiwi_world.blend.
//
// Sam, 2 Nov: "a curve based rail that I can easily modify, and then we
// should try and instance it, even if we instance in sections ... it needs
// to be well optimised for web". The curve is a Bezier in the blend's
// 'Monorail' collection; scripts/blender/monorail-sync.py writes its points,
// and where the pylons stand, to lib/world/monorailPath.js. This file turns
// that into pieces:
//
//   - the beam is cut into equal pieces of about PIECE metres along the
//     curve. A piece whose stretch of curve is straight is one instance of a
//     single beam piece; one on a bend is the same piece bent along the
//     curve, merged with the other bent ones in its section
//   - every piece starts with its joint, so the joints come with the pieces
//   - the pieces go into sections of about SECTION_PIECES, each drawn on its
//     own, so a section off screen is not drawn at all (today the loop is
//     one section: see SECTION_PIECES)
//   - the pylons are wherever the sync put them (it knows the ground and
//     the walls there), one instance each, and each is a box in the walls
//     the town is sent (monorailBlocks)
//
// Positions are the viewer's (x, y, z). The curve runs between the two
// beams at the height of their tops, where a train would run. Imports nothing, like wants.js, so the
// walls test and the town can read it.

import { MONORAIL_PATH } from './monorailPath.js'

// The sync spaces the pylons by the same numbers: a pylon under every
// PYLON_EVERY-th joint (scripts/blender/monorail-sync.py).
export const PIECE = 6
export const PYLON_EVERY = 3
// a section is about 720 m, so today's 545 m loop is one: three meshes in
// all. Measured 2 Nov, the monorail switched on and off in one page
// (docs/monorail.md): the shadow pass draws every section every frame
// wherever the camera looks, and from the middle of town the loop is on
// every side, so culling by section bought little and every mesh cost a
// draw. On a CPU slowed four times, 11 sections added 0.9 ms a frame, 6
// added 0.8, one added 0.4; the GPU did not tell them apart. A line much
// longer than the town would split again
export const SECTION_PIECES = 120

// A piece is straight when no point of its curve is further than this from
// the line between its ends: the joints hide that much.
const STRAIGHT_TOL = 0.03

// Sam, 2 Nov, with two photos of the Tokyo monorail: "more brutalist
// looking concrete pillars like this, and the tracks to feel like they
// have details", and two beams side by side, as theirs are. Each beam is
// about as deep as theirs for its width, and they run gauge apart, centre
// to centre, one either side of the curve; a train will run on either.
// The pylon is a thick column under a chamfered crossbeam that takes both.
export const BEAM = { width: 0.9, height: 1.4, gauge: 3.6 }
// Under each beam a steel bearing on a concrete seat, then the cap: a
// hammerhead across both beams, its ends square for capEdge and then
// sloping in to the column (Sam, 2 Nov, of the first one: "where the
// pillar connects to the rail needs work"; it was a thin wedge the column
// came through, and the beams sat on its lips). Then the column,
// eight-sided, down to the ground.
export const PYLON = { bearing: 0.06, seat: 0.22, capTop: 5.5, capBottom: 2.0, capLength: 2.4, capHeight: 1.7, capEdge: 0.7, radius: 0.85 }
// a pylon's box in the walls, a little wider than the column
const PYLON_HALF = 0.95

// Sam, 2 Nov, of the route: the line "going through the building, like the
// train in Chongqing Liziba Station ... build out the station through the
// building", and at its ends "tunnels in the moutains, so it goes through
// them and seemingly off the map entirely".
//
// A station is the passage through a building (an object with a
// 'monorail_station' property in the blend; the sync cuts its end walls):
// a floor slab the beams rest on, a platform either side at a train's
// floor, walls, a ceiling, and the frame round each opening. In metres
// from the beams' tops and from the middle of the line. The sync cuts the
// building to the same size (STATION_HALF, CUT_BELOW, CUT_ABOVE).
export const STATION = { half: 7.2, wall: 0.4, below: 2.0, platform: 1.0, inner: 3.35, ceiling: 4.4, above: 5.0, frame: 0.6 }
// A portal is where the line goes into a hill. It is modelled in the blend
// (monorail_portal, scripts/blender/monorail-portal.py; Sam, 2 Nov, of the
// first ones, built here out of boxes: "it needs to feel high quality, not
// thrown together"), and the sync stands it at each mouth with a cutting in
// front, its walls stepping down with the ground, and shapes the ground to
// both. They come to the site as public/models/monorailModels.glb. These
// are its sizes, for the walls the town is sent and for how far the track
// is drawn: the headwall's half width and thickness, the lining's depth (it
// falls to black by fadeEnd), and the cutting's walls: inner face, thickness.
export const PORTAL = { headHalf: 7.2, headThick: 1.0, depth: 8, fadeStart: 0.6, fadeEnd: 6.6, wallIn: 5.0, wallT: 0.7 }

// What stands along a station's platforms, by metres along it from where
// the line goes in: a column against the back wall every 7 m, a bench
// between each pair. The station's mesh (Monorail.jsx) and its walls
// (monorailStationSolids) both read it, so they cannot part company.
export function stationFurniture(len) {
  const columns = [], benches = []
  for (let x = 3.5; x < len - 1; x += 7) {
    columns.push([x - 0.3, x + 0.3])
    if (x + 3.5 < len - 1.5) benches.push([x + 2.7, x + 4.3])
  }
  return { columns, benches }
}

// The way up (Sam, 3 Nov, of how Pino and visitors get to the station:
// a lift tower). It stands just past one end of a station, on one side of
// the line, its top door opening onto that side's platform at its end and
// its bottom door on the side away from the line, onto the ground. The
// sync says which end and side (a 'monorail_lift' property on the station's
// building, 'out right': past the end the line comes out of, on the line's
// right going that way) and builds the tower to the ground there
// (scripts/blender/monorail-lift.py); the town carries him up and down in
// it (sim-server/src/world/Lift.js), and a visitor calls it with a button.
//
// In metres in the frame of the station's end: u along the line, outward
// from the end; d across it, from the middle of the line towards the
// lift's side; heights over the ground under the tower or, for the top
// landing, the platform's. The car is the shaft inside its walls.
export const LIFT = {
  u0: 0.45, u1: 3.55,          // the shaft's outer faces along the line (the station's frame takes the first 0.4)
  d0: 3.45, d1: 6.75,          // and across it: the platform's width, clear of a train's side
  wall: 0.35,
  sill: 0.15,                  // the bottom landing's floor over the ground
  lowDoor: 1.4, highDoor: 1.6, doorHeight: 2.3,
  top: 6.0,                    // the tower over the platform, machine room and all
  standOut: 1.2,               // where he waits for it, out from each door
  // the car's timings, which the town runs and the viewer draws: the doors
  // opening or closing, held open, and the ride between the landings
  doorMs: 1200, holdMs: 5000, rideMs: 8000,
}
// The ticket machine (Sam, 3 Nov: "lets add a ticket machine...same style
// as the rest of the world, eventually it will be his way of buying
// tickets to ride the train, for now, random old used tickets show up
// around it for him to collect"): against the back wall of the lift's
// platform, between the last bench and the last column, facing the line.
// Its middle and half sizes in the lift's frame. Old tickets lie round it
// in TICKET_FLOOR, off the yellow line and clear of the furniture.
export const MACHINE = { u: -3.9, d: 6.5, hu: 0.5, hd: 0.3, height: 1.85 }
export const TICKET_FLOOR = { u0: -7.5, u1: -0.8, d0: 3.9, d1: 6.0 }

/** A cubic Bezier spline as a dense polyline, the way Blender draws it. */
function polyline(points, closed) {
  const out = []
  const spans = closed ? points.length : points.length - 1
  for (let i = 0; i < spans; i++) {
    const a = points[i], b = points[(i + 1) % points.length]
    const p0 = [a[0], a[1], a[2]], p1 = [a[6], a[7], a[8]], p2 = [b[3], b[4], b[5]], p3 = [b[0], b[1], b[2]]
    // about a step every quarter metre of the control polygon
    const hull = dist(p0, p1) + dist(p1, p2) + dist(p2, p3)
    const steps = Math.max(8, Math.ceil(hull / 0.25))
    for (let k = 0; k < steps; k++) {
      const t = k / steps, u = 1 - t
      const w0 = u * u * u, w1 = 3 * u * u * t, w2 = 3 * u * t * t, w3 = t * t * t
      out.push([
        w0 * p0[0] + w1 * p1[0] + w2 * p2[0] + w3 * p3[0],
        w0 * p0[1] + w1 * p1[1] + w2 * p2[1] + w3 * p3[1],
        w0 * p0[2] + w1 * p1[2] + w2 * p2[2] + w3 * p3[2],
      ])
    }
  }
  const last = points[closed ? 0 : points.length - 1]
  out.push([last[0], last[1], last[2]])
  return out
}

function dist(a, b) { return Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]) }

/**
 * One line of track, measured along its length.
 * @returns {{ name, closed, length, at(s): number[], frame(s): { p, t, side, up } }}
 */
export function makeLine({ name, closed, points }) {
  const pts = polyline(points, closed)
  const S = [0]
  for (let i = 1; i < pts.length; i++) S.push(S[i - 1] + dist(pts[i - 1], pts[i]))
  const length = S[S.length - 1]

  const at = (s) => {
    if (closed) s = ((s % length) + length) % length
    else s = Math.min(length, Math.max(0, s))
    let lo = 0, hi = S.length - 1
    while (hi - lo > 1) { const mid = (lo + hi) >> 1; if (S[mid] <= s) lo = mid; else hi = mid }
    const span = S[hi] - S[lo] || 1, f = (s - S[lo]) / span
    const a = pts[lo], b = pts[hi]
    return [a[0] + (b[0] - a[0]) * f, a[1] + (b[1] - a[1]) * f, a[2] + (b[2] - a[2]) * f]
  }

  // The beam stays upright and pitches with the slope: no banking. Its
  // frame is the way it runs (t), across it, level (side), and up out of
  // its top face (up); t, up, side are a right-handed x, y, z.
  const frame = (s) => {
    const h = 0.25
    const lo = closed ? s - h : Math.max(0, s - h), hi = closed ? s + h : Math.min(length, s + h)
    const a = at(lo), b = at(hi)
    const t = norm([b[0] - a[0], b[1] - a[1], b[2] - a[2]])
    let side = [-t[2], 0, t[0]]
    if (Math.hypot(side[0], side[2]) < 1e-6) side = [1, 0, 0]
    side = norm(side)
    const up = cross(side, t)
    return { p: at(s), t, side, up }
  }

  return { name, closed, length, at, frame }
}

function norm(v) { const l = Math.hypot(v[0], v[1], v[2]) || 1; return [v[0] / l, v[1] / l, v[2] / l] }
function cross(a, b) { return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]] }

function straight(line, s0, s1) {
  const a = line.at(s0), b = line.at(s1)
  const ab = [b[0] - a[0], b[1] - a[1], b[2] - a[2]]
  const len2 = ab[0] * ab[0] + ab[1] * ab[1] + ab[2] * ab[2] || 1
  const n = Math.max(4, Math.ceil((s1 - s0) / 0.5))
  for (let k = 1; k < n; k++) {
    const p = line.at(s0 + ((s1 - s0) * k) / n)
    const f = ((p[0] - a[0]) * ab[0] + (p[1] - a[1]) * ab[1] + (p[2] - a[2]) * ab[2]) / len2
    const d = Math.hypot(p[0] - a[0] - ab[0] * f, p[1] - a[1] - ab[1] * f, p[2] - a[2] - ab[2] * f)
    if (d > STRAIGHT_TOL) return false
  }
  return true
}

/**
 * The whole monorail as pieces in sections.
 *
 * @returns {{ lines: Array<{ line, piece: number, sections: Array<{
 *   pieces: Array<{ i, s0, s1, straight: boolean }>,
 *   pylons: Array<{ x, z, ground, top, yaw }>,
 * }>, stations: Array<{ s0, s1, name }>, tunnels: Array<{ s, into, cut }> }>, pylons: number }}
 */
export function layout(path = MONORAIL_PATH) {
  const lines = []
  let pylonCount = 0
  for (let li = 0; li < (path?.lines || []).length; li++) {
    const spec = path.lines[li]
    if (!spec.points || spec.points.length < 2) continue
    const line = makeLine(spec)
    const n = Math.max(1, Math.round(line.length / PIECE))
    const piece = line.length / n
    const pieces = []
    for (let i = 0; i < n; i++) {
      const s0 = i * piece, s1 = (i + 1) * piece
      pieces.push({ i, s0, s1, straight: straight(line, s0, s1) })
    }
    // an open line ends in an end of its own
    if (!line.closed) pieces.push({ i: n, s0: line.length, s1: line.length, end: true, straight: false })
    // what is past the black at the back of a portal is never seen, so it is
    // not drawn; what is inside the tunnel goes dark with the lining (inside)
    const tunnels = (path.tunnels || []).filter((t) => t[0] === li).map(([, s, into, cut]) => ({ s, into, cut }))
    const into = (t, s) => (s - t.s) * t.into
    const hidden = (p) => tunnels.some((t) => Math.min(into(t, p.s0), into(t, p.s1)) >= PORTAL.depth)
    for (let k = pieces.length - 1; k >= 0; k--) if (hidden(pieces[k])) pieces.splice(k, 1)
    for (const p of pieces) {
      const t = tunnels.find((t) => Math.max(into(t, p.s0), into(t, p.s1)) > PORTAL.fadeStart)
      if (t) p.inside = t
    }

    const count = Math.max(1, Math.round(n / SECTION_PIECES))
    const sections = Array.from({ length: count }, (_, k) => ({ pieces: [], pylons: [] }))
    const sectionOf = (s) => Math.min(count - 1, Math.floor((s / line.length) * count))
    for (const p of pieces) sections[sectionOf(p.s0)].pieces.push(p)
    for (const [l, s, x, z, ground, top, yaw] of path.pylons || []) {
      if (l !== li) continue
      sections[sectionOf(s)].pylons.push({ x, z, ground, top, yaw })
      pylonCount++
    }
    const stations = (path.stations || []).filter((st) => st[0] === li).map(([, s0, s1, name]) => ({ s0, s1, name }))
    lines.push({ line, piece, sections, stations, tunnels })
  }
  return { lines, pylons: pylonCount }
}

/**
 * The pylons as boxes in the ground plane, the shape wantBlocks gives:
 * they go to the town with the walls, so he walks round them, and to the
 * walker, so a visitor does too. The beam is overhead and blocks nothing.
 */
export function monorailBlocks(path = MONORAIL_PATH) {
  const pylons = (path?.pylons || []).map(([l, s, x, z, ground, top, yaw], i) => ({
    name: `monorail_pylon_${i}`,
    cx: x, cz: z, hx: PYLON_HALF, hz: PYLON_HALF,
    ax: [Math.cos(yaw), -Math.sin(yaw)],
  }))
  // each portal's headwall, and the cutting's walls in front of it, with
  // their piers: in the portal's own axes (x into the hill, y across) and
  // then the town's
  const lines = (path?.lines || []).map((spec) => (spec.points?.length >= 2 ? makeLine(spec) : null))
  const portals = []
  ;(path?.tunnels || []).forEach(([l, s, into, cut], i) => {
    const line = lines[l]
    if (!line) return
    const { p, t } = line.frame(s)
    const len = Math.hypot(t[0], t[2]) || 1
    const T = [into * t[0] / len, into * t[2] / len], Y = [T[1], -T[0]]
    // ax is the box's own x in the town's (x, z), as the pylons' is: it was
    // [T[0], -T[1]], which only came out right because both tunnels run
    // along x
    const box = (name, x0, x1, y, hy) => ({
      name, cx: p[0] + T[0] * (x0 + x1) / 2 + Y[0] * y, cz: p[2] + T[1] * (x0 + x1) / 2 + Y[1] * y,
      hx: (x1 - x0) / 2 + 0.1, hz: hy + 0.1, ax: [T[0], T[1]],
    })
    portals.push(box(`monorail_portal_${i}`, 0, PORTAL.headThick, 0, PORTAL.headHalf))
    const mid = PORTAL.wallIn + PORTAL.wallT / 2
    for (const [k, side] of [[0, 1], [1, -1]]) portals.push(box(`monorail_portal_${i}_wall_${k}`, -(cut || 0) - 0.9, 0, side * mid, PORTAL.wallT / 2))
  })
  return [...pylons, ...portals]
}

/** How high the walker's boxes for the pylons go: up to the tallest cap. */
export function monorailBlockHeight(path = MONORAIL_PATH) {
  let h = 3
  for (const p of path?.pylons || []) h = Math.max(h, p[5] - p[4])
  return h
}

/**
 * Each lift, laid out in the town's (x, z): its frame, the car, where he
 * waits at each landing, the two floors, the machine on its platform and
 * the platform itself. `at(u, d)` is a point in the lift's frame.
 *
 * lifts in the path: [line, metres along it of the station's end it stands
 * past, out (1 past the end the line comes out of, -1 before the end it
 * goes in at), side (1 the line's right going out, -1 its left), the
 * ground under the tower].
 */
export function liftLayout(path = MONORAIL_PATH) {
  const lines = (path?.lines || []).map((spec) => (spec.points?.length >= 2 ? makeLine(spec) : null))
  const out = []
  ;(path?.lifts || []).forEach(([l, s, dir, sgn, ground], i) => {
    const line = lines[l]
    const st = (path.stations || []).find((x) => x[0] === l && (Math.abs(x[1] - s) < 0.5 || Math.abs(x[2] - s) < 0.5))
    if (!line || !st) return
    const { p, t, side } = line.frame(s)
    const tl = Math.hypot(t[0], t[2]) || 1
    const U = [dir * t[0] / tl, dir * t[2] / tl]
    const D = [sgn * side[0], sgn * side[2]]
    const at = (u, d) => [p[0] + U[0] * u + D[0] * d, p[2] + U[1] * u + D[1] * d]
    const frameOf = (x, z) => ({ u: (x - p[0]) * U[0] + (z - p[2]) * U[1], d: (x - p[0]) * D[0] + (z - p[2]) * D[1] })
    const len = st[2] - st[1]
    const L = LIFT
    const cu = (L.u0 + L.u1) / 2, cd = (L.d0 + L.d1) / 2
    const [cx, cz] = at(cu, cd)
    const [lx, lz] = at(cu, L.d1 + L.standOut)
    const [hx, hz] = at(-L.standOut - 0.3, cd)
    const [mx, mz] = at(MACHINE.u, MACHINE.d)
    const [ms, mt] = at(MACHINE.u, MACHINE.d - MACHINE.hd - 1.2)
    out.push({
      i, line: l, s, out: dir, side: sgn, ground,
      low: ground + L.sill, high: p[1] + STATION.platform,
      U, D, at, frameOf, len, station: st[3],
      car: { x: cx, z: cz, hu: (L.u1 - L.u0) / 2 - L.wall, hd: (L.d1 - L.d0) / 2 - L.wall },
      standLow: { x: lx, z: lz }, standHigh: { x: hx, z: hz },
      machine: { x: mx, z: mz, stand: { x: ms, z: mt } },
    })
  })
  return out
}

/**
 * Whether (x, z) is up on a lift's platform, the station's level, and
 * which lift's. The town's walking is flat: the platform is open ground to
 * it, shut in by walls on every side, and the lift is the one way between
 * it and the street. -1 when it is on the ground.
 */
export function liftLevelAt(x, z, lifts = liftLayout()) {
  for (const lf of lifts) {
    const { u, d } = lf.frameOf(x, z)
    if (u >= -lf.len - 0.3 && u <= LIFT.u0 && d >= STATION.inner - 0.3 && d <= STATION.half) return lf.i
  }
  return -1
}

/** Inside a lift's car, from the town's (x, z): the car, or null. */
export function liftCarAt(x, z, lifts = liftLayout()) {
  for (const lf of lifts) {
    const { u, d } = lf.frameOf(x, z)
    const c = lf.frameOf(lf.car.x, lf.car.z)
    if (Math.abs(u - c.u) <= lf.car.hu && Math.abs(d - c.d) <= lf.car.hd) return lf
  }
  return null
}

// A platform's own parts in a lift's frame, its walls ({ ..., y0, y1 }) and
// floors ({ ..., top }): the gates along its edge (stopping short of the
// steps), the end the line goes in at, the back wall with its columns and
// benches, the floor and the steps down to the beam. `far` is the platform
// across the line from the lift's, which nobody but a visitor can reach
// (along the beams): it has no lift, so its other end is shut too.
function platformParts(lf, far) {
  const solids = [], floors = []
  const D = far ? [-lf.D[0], -lf.D[1]] : lf.D
  const at = (u, d) => [lf.at(u, 0)[0] + D[0] * d, lf.at(u, 0)[1] + D[1] * d]
  const tag = `monorail_lift_${lf.i}_${far ? 'far_' : ''}`
  const box = (list, name, u0, u1, d0, d1, more) => {
    const [cx, cz] = at((u0 + u1) / 2, (d0 + d1) / 2)
    list.push({ name: tag + name, cx, cz, hx: (u1 - u0) / 2, hz: (d1 - d0) / 2, ax: lf.U, ...more })
  }
  const H = lf.high, G = lf.ground
  const { inner, half, wall } = STATION
  const back = half - wall, ceiling = H - STATION.platform + STATION.ceiling
  const u = (x) => (lf.out === 1 ? x - lf.len : -x)   // metres along the station to the lift's frame
  const span = (a, b) => [Math.min(u(a), u(b)), Math.max(u(a), u(b))]
  box(solids, 'gates', ...span(STATION_STEPS.gap, lf.len), inner, inner + 0.12, { y0: G, y1: H + 1.3 })
  box(solids, 'end', -lf.len - 0.05, -lf.len + 0.25, inner, half, { y0: G, y1: H + 1.2 })
  if (far) box(solids, 'end_out', -0.25, 0.05, inner, half, { y0: G, y1: H + 1.2 })
  box(solids, 'back', -lf.len, 0, back, half, { y0: G, y1: ceiling })
  const f = stationFurniture(lf.len)
  f.columns.forEach(([a, b], k) => box(solids, `column_${k}`, ...span(a, b), back - 0.6, back, { y0: G, y1: ceiling }))
  f.benches.forEach(([a, b], k) => box(solids, `bench_${k}`, ...span(a, b), back - 1.1, back - 0.6, { y0: G, y1: H + 0.45 }))
  box(floors, 'platform', -lf.len, 0, inner, back, { top: H })
  // the steps down to the beam at the platform's far end, falling towards it
  const S = STATION_STEPS, run = (S.to - S.from) / S.treads, beamOut = BEAM.gauge / 2 + BEAM.width / 2
  for (let k = 0; k < S.treads; k++) {
    const a = u(S.from + run * k), b = u(S.from + run * (k + 1))
    box(floors, `tread_${k}`, Math.min(a, b), Math.max(a, b), beamOut - 0.02, inner + 0.02, { top: H - (STATION.platform * (S.treads - k)) / (S.treads + 1) })
  }
  return { solids, floors }
}

/**
 * The walls up at a lift's platform and round its tower, with how high
 * each goes ({ name, cx, cz, hx, hz, ax, y0, y1 }): to the town, which reads
 * them flat, and to the walker, who walks under or over what is out of
 * reach. Round the platform: its parts (platformParts) and the ticket
 * machine. Round the tower: its four walls, each door shut over and under
 * so the town sees the shaft closed (it carries him through it) and a
 * visitor sees the doors. The doors themselves open and shut in the viewer.
 */
export function monorailStationSolids(path = MONORAIL_PATH) {
  const out = []
  for (const lf of liftLayout(path)) {
    const L = LIFT, H = lf.high, G = lf.ground, top = H + L.top
    const box = (name, u0, u1, d0, d1, y0, y1) => {
      const [cx, cz] = lf.at((u0 + u1) / 2, (d0 + d1) / 2)
      out.push({ name: `monorail_lift_${lf.i}_${name}`, cx, cz, hx: (u1 - u0) / 2, hz: (d1 - d0) / 2, ax: lf.U, y0, y1 })
    }
    const { inner, half } = STATION
    out.push(...platformParts(lf, false).solids)
    box('machine', MACHINE.u - MACHINE.hu, MACHINE.u + MACHINE.hu, MACHINE.d - MACHINE.hd, MACHINE.d + MACHINE.hd, G, H + MACHINE.height)
    // the short way between the platform's end and the lift's top door, at
    // its own height: on the ground it would be a wall nobody can see
    box('step_line', 0, L.u0, inner, L.d0, H - 0.5, H + 2.6)
    box('step_back', 0, L.u0, L.d1, half, H - 0.5, H + 2.6)
    // the tower: the wall by the platform, with the top door in it
    const cd = (L.d0 + L.d1) / 2, cu = (L.u0 + L.u1) / 2
    const hd = L.highDoor / 2, ld = L.lowDoor / 2
    box('wall_near_a', L.u0, L.u0 + L.wall, L.d0, cd - hd, G, top)
    box('wall_near_b', L.u0, L.u0 + L.wall, cd + hd, L.d1, G, top)
    box('wall_near_under', L.u0, L.u0 + L.wall, cd - hd, cd + hd, G, H)
    box('wall_near_over', L.u0, L.u0 + L.wall, cd - hd, cd + hd, H + L.doorHeight, top)
    // the far wall, the wall by the line, and the wall on the ground's side with the bottom door in it
    box('wall_far', L.u1 - L.wall, L.u1, L.d0, L.d1, G, top)
    box('wall_line', L.u0, L.u1, L.d0, L.d0 + L.wall, G, top)
    box('wall_out_a', L.u0, cu - ld, L.d1 - L.wall, L.d1, G, top)
    box('wall_out_b', cu + ld, L.u1, L.d1 - L.wall, L.d1, G, top)
    box('wall_out_over', cu - ld, cu + ld, L.d1 - L.wall, L.d1, lf.low + L.doorHeight, top)
  }
  return out
}

/**
 * The floors up there, for a visitor's feet and for where he is drawn
 * ({ name, cx, cz, hx, hz, ax, top }): the platform, its steps and the step
 * to the lift's door at the station's level, and the bottom landing's sill.
 * The car's floor moves, and the viewer gives it.
 */
export function monorailStationFloors(path = MONORAIL_PATH) {
  const out = []
  for (const lf of liftLayout(path)) {
    const L = LIFT
    const floor = (name, u0, u1, d0, d1, top) => {
      const [cx, cz] = lf.at((u0 + u1) / 2, (d0 + d1) / 2)
      out.push({ name: `monorail_lift_${lf.i}_${name}`, cx, cz, hx: (u1 - u0) / 2, hz: (d1 - d0) / 2, ax: lf.U, top })
    }
    out.push(...platformParts(lf, false).floors)
    floor('step', -0.05, L.u0 + 0.02, STATION.inner, STATION.half, lf.high)
    const cu = (L.u0 + L.u1) / 2
    floor('sill', cu - L.lowDoor / 2, cu + L.lowDoor / 2, L.d1 - L.wall - 0.02, L.d1 + 0.25, lf.low)
  }
  return out
}

/**
 * What only a visitor can reach in a station, off the beams: the platform
 * across the line and the trackbed between the two, the floor the beams
 * rest on. Fallen in there, a visitor walks out at either end and down to
 * the street, rather than through the flats' floor into a room of walls.
 */
export function monorailStationVisitorParts(path = MONORAIL_PATH) {
  const solids = [], floors = []
  for (const lf of liftLayout(path)) {
    const far = platformParts(lf, true)
    solids.push(...far.solids)
    floors.push(...far.floors)
    const [cx, cz] = lf.at(-lf.len / 2, 0)
    floors.push({ name: `monorail_lift_${lf.i}_trackbed`, cx, cz, hx: lf.len / 2, hz: STATION.inner, ax: lf.U, top: lf.high - STATION.platform - BEAM.height - STATION.below })
  }
  return { solids, floors }
}

// The steps down from a platform's far end onto the beam beside it, for
// visitors (Sam, 3 Nov: "I think the monorail train lines should be
// colision objects you can walk along"): steel treads off the platform's
// edge at the end the line goes in at, where the gates stop, down to the
// beam's top. Metres along the station from that end, and the treads'
// width across, between the platform's edge and the beam's outer side.
export const STATION_STEPS = { from: 0.3, to: 2.3, treads: 4, gap: 2.6 }

/** Where a car is, what it is doing and how open each landing's doors are, at the town's time `now`. */
export function liftNow(lf, state, now) {
  const L = LIFT
  const levelY = (k) => (k ? lf.high : lf.low)
  const st = state || {}
  const since = Number(st.since) || 0
  let t = Math.max(0, now - since)
  // what comes next on its own, by the clock, in case word of it is late:
  // a ride ends in the doors opening, the doors open, the doors shut
  let phase = st.phase
  if (phase === 'moving' && t >= L.rideMs) { phase = 'opening'; t -= L.rideMs }
  if (phase === 'opening' && t >= L.doorMs) { phase = 'open'; t -= L.doorMs }
  if (phase === 'closing' && t >= L.doorMs) phase = 'shut'
  let y = levelY(st.level ? 1 : 0)
  let open = 0
  if (phase === 'moving') {
    const f = Math.min(1, t / L.rideMs)
    const e = f * f * (3 - 2 * f)
    y = levelY(st.from ? 1 : 0) + (levelY(st.to ? 1 : 0) - levelY(st.from ? 1 : 0)) * e
  } else if (phase === 'opening') open = Math.min(1, t / L.doorMs)
  else if (phase === 'open') open = 1
  else if (phase === 'closing') open = Math.max(0, 1 - t / L.doorMs)
  const at = phase === 'moving' ? -1 : (st.level ? 1 : 0)
  return { y, phase: phase || 'shut', moving: phase === 'moving', doors: [at === 0 ? open : 0, at === 1 ? open : 0] }
}

/**
 * The beams' tops as floors for a visitor's feet ({ name, cx, cz, hx, hz,
 * ax, top }): never sent to the town and never under him, so he is not
 * drawn up there and walks under them as before. A straight piece is one
 * floor a beam; a bent one, three. They stop a few metres into each
 * tunnel, where a wall (monorailVisitorSolids) keeps a visitor out of the
 * dark.
 */
export function monorailBeamFloors(path = MONORAIL_PATH) {
  const out = []
  const { lines } = layout(path)
  for (const { line, sections, tunnels } of lines) {
    const deep = (s) => tunnels.some((t) => (s - t.s) * t.into > TUNNEL_WALK)
    for (const sec of sections) {
      for (const pc of sec.pieces) {
        if (pc.end) continue
        const n = pc.straight ? 1 : 3
        for (let k = 0; k < n; k++) {
          const sa = pc.s0 + ((pc.s1 - pc.s0) * k) / n, sb = pc.s0 + ((pc.s1 - pc.s0) * (k + 1)) / n
          if (deep(sa) || deep(sb)) continue
          const a = line.at(sa), b = line.at(sb)
          const dx = b[0] - a[0], dz = b[2] - a[2], len = Math.hypot(dx, dz) || 1
          const { side } = line.frame((sa + sb) / 2)
          for (const g of [1, -1]) {
            out.push({
              name: `monorail_beam_${pc.i}_${k}_${g > 0 ? 'r' : 'l'}`,
              cx: (a[0] + b[0]) / 2 + side[0] * g * BEAM.gauge / 2, cz: (a[2] + b[2]) / 2 + side[2] * g * BEAM.gauge / 2,
              hx: len / 2 + 0.03, hz: BEAM.width / 2, ax: [dx / len, dz / len], top: (a[1] + b[1]) / 2,
            })
          }
        }
      }
    }
  }
  return out
}
// how far into a tunnel the beams can be walked: the lining is still lit there
const TUNNEL_WALK = 3

/**
 * Walls for visitors alone, never sent to the town ({ name, cx, cz, hx, hz,
 * ax, y0, y1 }): across each tunnel where the walkable beams stop.
 */
export function monorailVisitorSolids(path = MONORAIL_PATH) {
  const out = []
  const lines = (path?.lines || []).map((spec) => (spec.points?.length >= 2 ? makeLine(spec) : null))
  ;(path?.tunnels || []).forEach(([l, s, into], i) => {
    const line = lines[l]
    if (!line) return
    const at = s + into * (TUNNEL_WALK + 0.2)
    const { p, t } = line.frame(at)
    const len = Math.hypot(t[0], t[2]) || 1
    out.push({ name: `monorail_tunnel_${i}_stop`, cx: p[0], cz: p[2], hx: 0.15, hz: PORTAL.headHalf, ax: [t[0] / len, t[2] / len], y0: p[1] - 3, y1: p[1] + 5 })
  })
  return out
}
